// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Playfield electromagnet (M1-19).
//
// A real magnet's pull falls off roughly as the inverse square of distance, which diverges
// at the core. The divergence is not a modelling nicety to ignore: with a 1 ms step, a
// force that grows without bound will fling a ball out at absurd speed the moment it gets
// close, which is the exact opposite of the intended effect.
//
// The fix is a **softening radius** — a distance below which the field is treated as
// uniform. Physically it stands in for the fact that a coil is not a point; numerically it
// caps the force at `peakForce` and keeps the integration stable. One ball radius is a
// natural choice, since the ball centre can never actually reach the coil face.

import type { Ball } from '../Ball'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { BALL_RADIUS } from '../constants'

export interface MagnetConfig {
  readonly id: string
  readonly centre: readonly [number, number]
  /** Beyond this the field is exactly zero, so the broadphase-free test stays cheap. */
  readonly radius: number
  /** Force at the core, newtons. 0.9 N on an 80.6 g ball is about 11 m/s² — a strong magnet. */
  readonly peakForce: number
  /** Optional deflection visit: release after this many fixed steps, rearm after all balls leave. */
  readonly releaseAfterSteps?: number
  /** Distance below which the field stops growing, metres. */
  readonly softening?: number
  /** Height band the ball must be in for the field to reach it. */
  readonly zRange?: readonly [number, number]
}

export class Magnet extends Device {
  readonly kind = 'magnet'
  override readonly wantsForce = true

  readonly cx: number
  readonly cy: number
  readonly radius: number

  private readonly peakForce: number
  private readonly soften2: number
  private readonly zMin: number
  private readonly zMax: number
  private readonly releaseAfterSteps: number
  private pullStarted = -1
  private released = false

  /** Coil energised. Owned by the rule engine. */
  on = false

  /** Effective coil state; the requested power can stay on while a visit is released. */
  get fieldOn(): boolean { return this.on && !this.released }

  protected override ownState(): DeviceState {
    return this.releaseAfterSteps>0 ? { on:this.on,pullStarted:this.pullStarted,released:this.released } : { on:this.on }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.on = state['on'] === true
    this.pullStarted = typeof state['pullStarted']==='number' ? state['pullStarted'] : -1
    this.released = state['released'] === true
  }

  constructor(config: MagnetConfig) {
    super(config.id)
    this.cx = config.centre[0]
    this.cy = config.centre[1]
    this.radius = config.radius
    this.setOrigin(this.cx, this.cy)
    this.peakForce = config.peakForce
    this.releaseAfterSteps = config.releaseAfterSteps ?? 0
    const s = config.softening ?? BALL_RADIUS
    this.soften2 = s * s
    this.zMin = config.zRange ? config.zRange[0] : -Infinity
    this.zMax = config.zRange ? config.zRange[1] : Infinity
  }

  override step(world: World, _dt: number): void {
    if(this.releaseAfterSteps<=0)return
    if(!this.on){this.pullStarted=-1;this.released=false;return}
    let near=false,inside=false
    const rearmRadius=this.radius+.005
    for(const ball of world.balls){
      if(!ball.active||ball.heldBy>=0||ball.z<this.zMin||ball.z>this.zMax)continue
      const dx=ball.x-this.cx,dy=ball.y-this.cy,d2=dx*dx+dy*dy
      if(d2<=rearmRadius*rearmRadius)near=true
      if(d2<=this.radius*this.radius)inside=true
    }
    if(!near){this.pullStarted=-1;this.released=false;return}
    if(!this.released&&inside&&this.pullStarted<0)this.pullStarted=world.stepIndex
    if(this.pullStarted>=0&&world.stepIndex-this.pullStarted>=this.releaseAfterSteps)this.released=true
  }

  override applyForce(world: World, ball: Ball, dt: number): void {
    if (!this.on) return
    if (ball.z < this.zMin || ball.z > this.zMax) return

    const dx = this.cx - ball.x
    const dy = this.cy - ball.y
    const d2 = dx * dx + dy * dy
    if (d2 > this.radius * this.radius) return
    if (d2 < 1e-18) return

    let gain=1
    if(this.releaseAfterSteps>0){
      if(this.released)return
      if(this.pullStarted<0)this.pullStarted=world.stepIndex
      const age=world.stepIndex-this.pullStarted
      if(age>=this.releaseAfterSteps){this.released=true;return}
      gain=Math.min(1,(1-age/this.releaseAfterSteps)/.4)
    }

    // F = peak · s² / (d² + s²): peak at the core, inverse-square far from it, finite
    // everywhere in between.
    const force = gain * (this.peakForce * this.soften2) / (d2 + this.soften2)
    const accel = force * ball.invMass * dt
    const inv = 1 / Math.sqrt(d2)
    ball.vx += dx * inv * accel
    ball.vy += dy * inv * accel
  }

  override reset(): void {
    this.on = false
    this.pullStarted = -1
    this.released = false
  }
}
